import React, { useEffect, useRef } from 'react';
import { MapNode } from './useMapLayout';
import { NODE_TAB_MAP } from './constants';
import { FiTarget, FiExternalLink, FiCopy, FiMaximize2, FiRefreshCw, FiZoomIn } from 'react-icons/fi';

interface Props {
  x: number;
  y: number;
  node: MapNode | null;
  darkMode: boolean;
  onClose: () => void;
  onFlyTo: (nodeId: string) => void;
  onSelect: (nodeId: string) => void;
  onJumpToTab: (tabIndex: number) => void;
  onFitAll: () => void;
  onResetZoom: () => void;
  onCopyTransform: () => void;
}

interface MenuItem {
  icon: React.ReactNode;
  label: string;
  action: () => void;
  danger?: boolean;
  dividerBefore?: boolean;
}

const MapContextMenu: React.FC<Props> = ({
  x, y, node, darkMode, onClose,
  onFlyTo, onSelect, onJumpToTab,
  onFitAll, onResetZoom, onCopyTransform,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [onClose]);

  const menuW = 200;
  const menuH = 240;
  const clampedX = Math.min(x, window.innerWidth - menuW - 8);
  const clampedY = Math.min(y, window.innerHeight - menuH - 8);

  const items: MenuItem[] = node
    ? [
        {
          icon: <FiTarget size={12} />,
          label: 'Fly to node',
          action: () => { onFlyTo(node.id); onClose(); },
        },
        {
          icon: <FiExternalLink size={12} />,
          label: 'Jump to tab',
          action: () => {
            const mapping = NODE_TAB_MAP[node.type];
            if (mapping) onJumpToTab(mapping.tabIndex);
            onClose();
          },
        },
        {
          icon: <FiCopy size={12} />,
          label: 'Copy label',
          action: () => {
            navigator.clipboard?.writeText(node.label).catch(() => {});
            onClose();
          },
          dividerBefore: true,
        },
        {
          icon: <FiCopy size={12} />,
          label: 'Copy node ID',
          action: () => {
            navigator.clipboard?.writeText(node.id).catch(() => {});
            onClose();
          },
        },
      ]
    : [
        {
          icon: <FiMaximize2 size={12} />,
          label: 'Fit all nodes',
          action: () => { onFitAll(); onClose(); },
        },
        {
          icon: <FiRefreshCw size={12} />,
          label: 'Reset zoom',
          action: () => { onResetZoom(); onClose(); },
        },
        {
          icon: <FiZoomIn size={12} />,
          label: 'Copy transform',
          action: () => { onCopyTransform(); onClose(); },
          dividerBefore: true,
        },
      ];

  return (
    <div
      ref={menuRef}
      style={{
        position: 'fixed',
        left: clampedX,
        top: clampedY,
        width: menuW,
        zIndex: 9999,
      }}
      className={`rounded-lg border shadow-2xl overflow-hidden ${
        darkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
      }`}
    >
      {/* Header */}
      <div className={`px-3 py-1.5 border-b ${
        darkMode ? 'border-gray-700 bg-gray-800/60' : 'border-gray-100 bg-gray-50'
      }`}>
        <span className={`font-semibold truncate block ${
          darkMode ? 'text-gray-400' : 'text-gray-500'
        }`} style={{ fontSize: 10 }}>
          {node ? node.label : 'Canvas'}
        </span>
        {node && (
          <span className="opacity-50 block" style={{ fontSize: 8 }}>
            {node.type.replace(/_/g, ' ')}
          </span>
        )}
      </div>

      {/* Items */}
      <div className="py-0.5">
        {items.map((item, i) => (
          <React.Fragment key={i}>
            {item.dividerBefore && (
              <div className={`my-0.5 border-t ${darkMode ? 'border-gray-700' : 'border-gray-100'}`} />
            )}
            <button
              onClick={item.action}
              className={`w-full flex items-center gap-2 px-3 py-1.5 text-left transition-colors ${
                item.danger
                  ? darkMode
                    ? 'text-red-400 hover:bg-red-900/30'
                    : 'text-red-600 hover:bg-red-50'
                  : darkMode
                    ? 'text-gray-300 hover:bg-gray-700'
                    : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span className="flex-shrink-0 opacity-70">{item.icon}</span>
              <span style={{ fontSize: 11 }}>{item.label}</span>
            </button>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default MapContextMenu;
